import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { printManifestoMinutaPdf } from '@/lib/manifestoMinutaPrint';
import { downloadManifestoMinutaDocx } from '@/lib/manifestoMinutaDocx';
import { buildImpugnacaoIaPromptFromDocumentos, SCHEMA_IMPUGNACAO_IA } from '@/lib/manifestoImpugnacaoIa';
import {
  buildQualificacaoImpugnanteParagrafo,
  montarMinutaImpugnacaoCompleta,
} from '@/lib/manifestoImpugnacaoQualificacao';
import { useAuth } from '@/lib/AuthContext';
import { api, entities, getDisplayMessage, uploadFile } from '@/api/apiClient';
import { toast } from 'sonner';
import {
  AlertTriangle,
  FileSearch,
  FileText,
  FileUp,
  Loader2,
  Shield,
  ShieldAlert,
  Sparkles,
  Trash2,
} from 'lucide-react';

/** Quatro fases lineares: envio → IA → revisão → protocolo (dados do edital vinculado e da empresa vêm do cadastro). */
const FASES_IMPUGNACAO = [
  {
    key: 'envio',
    titulo: 'Envie o Edital',
    descricao: 'Upload do edital, ETP, Termo de Referência e demais documentos (PDF).',
    Icon: FileSearch,
  },
  {
    key: 'ia',
    titulo: 'IA Analisa Irregularidades',
    descricao: 'A IA identifica cláusulas restritivas, ilegalidades e pontos de atenção.',
    Icon: AlertTriangle,
  },
  {
    key: 'revisao',
    titulo: 'Revise a Minuta',
    descricao: 'Selecione e inclua as teses mais relevantes para sua impugnação.',
    Icon: FileText,
  },
  {
    key: 'protocolo',
    titulo: 'Baixe e Protocole',
    descricao: 'Documento editável pronto para revisão jurídica e protocolo.',
    Icon: Shield,
  },
];

const SLOT_META = {
  edital: { label: 'Edital', order: 0 },
  etp: { label: 'ETP (Estudo Técnico Preliminar)', order: 1 },
  termo_referencia: { label: 'Termo de Referência', order: 2 },
  outros: { label: 'Demais documentos', order: 3 },
};

const ANALISE_IA_INDISPONIVEL_MSG =
  'Serviço de IA indisponível. Verifique a conexão e a configuração em Super Admin > Config. IA Editais.';

/** Igual a `MANIFESTO_PLACEHOLDER_PECA` no servidor — nova manifestação não deve abrir na fase de revisão. */
const MANIFESTO_PECA_PLACEHOLDER =
  '(Rascunho — preencha o conteúdo da peça ou use "Gerar rascunho com IA" se disponível.)';

function normalizarConteudoMinutaServidor(raw) {
  const t = String(raw ?? '').trim();
  if (!t || t === MANIFESTO_PECA_PLACEHOLDER) return '';
  return t;
}

function formatElapsed(seconds) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function StepperImpugnacao({ currentIndex }) {
  return (
    <div className="relative">
      <div
        className="hidden md:block absolute left-0 right-0 top-[2.25rem] h-px bg-blue-200 z-0"
        aria-hidden
      />
      <ol className="relative z-[1] grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {FASES_IMPUGNACAO.map((fase, idx) => {
          const Icon = fase.Icon;
          const ativo = idx === currentIndex;
          const concluida = idx < currentIndex;
          return (
            <li key={fase.key}>
              <div
                className={`h-full rounded-lg border bg-white p-3 shadow-sm transition-colors ${
                  ativo
                    ? 'border-blue-500 ring-2 ring-blue-200 bg-blue-50/80'
                    : concluida
                      ? 'border-blue-200 bg-blue-50/40'
                      : 'border-slate-200 bg-muted/10'
                }`}
              >
                <div className="flex items-start gap-2">
                  <span
                    className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-xs font-bold tabular-nums ${
                      ativo ? 'bg-blue-600 text-white' : concluida ? 'bg-blue-100 text-blue-800' : 'bg-slate-100 text-slate-600'
                    }`}
                  >
                    {String(idx + 1).padStart(2, '0')}
                  </span>
                  <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex items-center gap-1.5">
                      <Icon className={`h-4 w-4 shrink-0 ${ativo ? 'text-blue-700' : 'text-slate-500'}`} aria-hidden />
                      <p className="text-xs font-semibold text-slate-900 leading-tight">{fase.titulo}</p>
                    </div>
                    <p className="text-[11px] leading-snug text-slate-600">{fase.descricao}</p>
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function parseObservacoesImpugnacaoSalvas(raw) {
  if (raw == null) return null;
  const s = typeof raw === 'string' ? raw.trim() : '';
  if (!s || s[0] !== '{') return null;
  try {
    const o = JSON.parse(s);
    if (o && o.tipo === 'impugnacao' && o.v === 1) return o;
    return null;
  } catch {
    return null;
  }
}

function restoreIssuesFromStorage(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  const first = rows[0];
  if (first && typeof first === 'object' && 'detalhe' in first) {
    return rows.map((row, idx) => ({
      id: String(row.id || `irr-${idx + 1}`),
      titulo: row.titulo || `Irregularidade ${idx + 1}`,
      severidade: ['Alta', 'Média', 'Baixa'].includes(row.severidade) ? row.severidade : 'Média',
      fundamento: row.fundamento || '',
      detalhe: row.detalhe || '',
      pedido: row.pedido || '',
    }));
  }
  return normalizeIrregularidades(rows);
}

function normalizeIrregularidades(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.map((row, idx) => {
    const id = (row.id && String(row.id).trim()) || `irr-${idx + 1}`;
    const titulo = row.titulo || `Irregularidade ${idx + 1}`;
    const sev = String(row.severidade || 'Média').trim();
    const severidade = ['Alta', 'Média', 'Baixa'].includes(sev) ? sev : 'Média';
    const fundamento = [row.fundamento_legal, row.referencia_tcu_sumula_acordao].filter(Boolean).join(' · ');
    const detalhe = [row.tipo_vicio ? `Tipo: ${row.tipo_vicio}` : '', row.trecho_ou_fato_edital || '']
      .filter(Boolean)
      .join('\n');
    const pedido = row.pedido_correcao || '';
    return { id, titulo, severidade, fundamento, detalhe, pedido };
  });
}

function sortAttachmentsForInvoke(list) {
  const ready = list.filter((a) => a.file_url && !a.uploading);
  return [...ready].sort((a, b) => {
    const oa = SLOT_META[a.slot]?.order ?? 9;
    const ob = SLOT_META[b.slot]?.order ?? 9;
    if (oa !== ob) return oa - ob;
    return String(a.id).localeCompare(String(b.id));
  });
}

function newId() {
  return typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `att-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/** Chave estável para detectar mudanças reais no servidor (re-hidratar após IA/salvar/refetch). */
function buildManifestoDetailSnapshot(manifestacaoId, det) {
  if (!det || String(det.id) !== String(manifestacaoId)) return null;
  const p0 = Array.isArray(det.pecas) && det.pecas.length ? det.pecas[0] : null;
  const obsStr = typeof det.observacoes === 'string' ? det.observacoes : '';
  return [
    String(manifestacaoId),
    String(det.updatedAt ?? ''),
    p0?.versao ?? '',
    String(p0?.createdAt ?? ''),
    obsStr.length,
    obsStr.slice(0, 240),
  ].join('\u001e');
}

export default function ManifestoImpugnacaoFlow({
  onSalvarRascunho,
  onSalvarFinal,
  salvandoFinal = false,
  onAvancar,
  editalLabel,
  editalId,
  manifestacaoId,
}) {
  const [attachments, setAttachments] = useState([]);
  const [sugestaoImpugnacao, setSugestaoImpugnacao] = useState('');
  const [issues, setIssues] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [stepIndex, setStepIndex] = useState(0);
  const [minutaText, setMinutaText] = useState('');
  const [iaLoading, setIaLoading] = useState(false);
  const [gerandoDocx, setGerandoDocx] = useState(false);
  const [iaMeta, setIaMeta] = useState(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const abortRef = useRef(null);
  const runInFlightRef = useRef(false);
  const pickSlotRef = useRef(null);
  const fileInputRef = useRef(null);
  const ultimoSnapshotServidorRef = useRef(null);

  const queryClient = useQueryClient();
  const { data: detalheServidor } = useQuery({
    queryKey: ['manifesto', 'detail', editalId, manifestacaoId],
    queryFn: () => entities.Manifesto.getManifestacao(editalId, manifestacaoId),
    enabled: Boolean(editalId && manifestacaoId),
  });
  const { empresa } = useAuth();
  const empresaIdConfig = empresa?.id;
  const { data: configs = [] } = useQuery({
    queryKey: ['configuracao-empresa', empresaIdConfig],
    queryFn: () => entities.ConfiguracaoEmpresa.list(),
    enabled: Boolean(empresaIdConfig),
  });
  const empresaConfigRow = configs[0];
  const qualificacaoParagrafo = useMemo(
    () => buildQualificacaoImpugnanteParagrafo(empresaConfigRow),
    [empresaConfigRow]
  );

  const selectedCount = selected.size;

  const uploadingAny = useMemo(() => attachments.some((a) => a.uploading), [attachments]);
  const hasReadyPdfs = useMemo(() => attachments.some((a) => a.file_url && !a.uploading), [attachments]);
  const canRunIa = !!editalId && !!manifestacaoId && hasReadyPdfs && !uploadingAny;

  const resumo = useMemo(() => {
    const base = `Impugnação — ${selectedCount} ponto(s) selecionado(s) para a minuta.`;
    if (iaMeta?.resumo_executivo) return `${base} ${iaMeta.resumo_executivo}`;
    return base;
  }, [selectedCount, iaMeta?.resumo_executivo]);

  const faseAtual = FASES_IMPUGNACAO[stepIndex] ?? FASES_IMPUGNACAO[0];
  const etapaTitulo = `Fase ${stepIndex + 1} — ${faseAtual.titulo}`;
  const etapaDescricao =
    stepIndex === 0
      ? 'Envie os PDFs do instrumento convocatório e documentos de apoio. Os dados do edital já vinculado a esta manifestação e o cadastro da empresa (Configurações) serão usados na qualificação e na minuta — não é necessário redigitá-los aqui.'
      : stepIndex === 1
        ? 'A análise usa somente o texto extraído dos PDFs enviados (não utiliza o processamento em Novo Edital/Cotação). Opcionalmente oriente a IA no campo abaixo e execute «Realizar análise IA».'
        : stepIndex === 2
          ? 'Selecione as irregularidades que permanecem na peça, edite a minuta e revise o texto antes de seguir para download e protocolo.'
          : 'Revise a minuta final, exporte em .DOCX ou imprima no padrão do sistema para protocolo externo.';

  const processStatusLabel = useMemo(() => {
    if (!iaLoading) return '';
    if (elapsedSeconds < 25) return 'Extraindo texto dos PDFs no servidor e montando o contexto para a IA…';
    return 'Analisando documentação e gerando irregularidades e minuta fundamentada…';
  }, [iaLoading, elapsedSeconds]);

  useEffect(() => {
    if (!iaLoading) return;
    setElapsedSeconds(0);
    const t = setInterval(() => setElapsedSeconds((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, [iaLoading]);

  const runImpugnacaoIa = useCallback(
    async () => {
      if (!editalId || !manifestacaoId) {
        toast.error('Manifestação ou edital não identificados.');
        return;
      }
      const sorted = sortAttachmentsForInvoke(attachments);
      if (!sorted.length) {
        toast.error('Envie ao menos um PDF para análise.');
        return;
      }
      if (runInFlightRef.current) return;

      abortRef.current?.abort();
      const ac = new AbortController();
      abortRef.current = ac;

      runInFlightRef.current = true;
      setIaLoading(true);
      try {
        const docList = sorted.map((a, i) => `${i + 1}. ${SLOT_META[a.slot]?.label || a.slot}: ${a.nome}`).join('\n');
        const prompt = buildImpugnacaoIaPromptFromDocumentos(editalLabel, docList, sugestaoImpugnacao.trim());
        const file_urls = sorted.map((a) => a.file_url);

        const result = await api.post(
          '/api/integrations/invoke-llm',
          {
            prompt,
            file_urls,
            response_json_schema: SCHEMA_IMPUGNACAO_IA,
          },
          { signal: ac.signal }
        );

        const out = result?.output;
        if (!out || typeof out !== 'object') {
          toast.error('A IA não retornou dados estruturados. Tente novamente.');
          return;
        }

        const list = normalizeIrregularidades(out.irregularidades);
        setIssues(list);
        const ids = new Set(list.map((i) => i.id));
        setSelected(ids.size ? ids : new Set());

        const cabecalho =
          typeof out.cabecalho_institucional === 'string' && out.cabecalho_institucional.trim()
            ? out.cabecalho_institucional.trim()
            : '';
        const corpoMinuta =
          typeof out.minuta_impugnacao === 'string' && out.minuta_impugnacao.trim()
            ? out.minuta_impugnacao.trim()
            : '';
        const textoMinutaCompleto = montarMinutaImpugnacaoCompleta(
          cabecalho,
          qualificacaoParagrafo,
          corpoMinuta
        );
        if (textoMinutaCompleto) setMinutaText(textoMinutaCompleto);

        const iaMetaNext = {
          resumo_executivo: out.resumo_executivo || '',
          confianca_0_100: typeof out.confianca_0_100 === 'number' ? out.confianca_0_100 : null,
          campos_edital_utilizados: typeof out.campos_edital_utilizados === 'number' ? out.campos_edital_utilizados : null,
          checklist_conformidade: Array.isArray(out.checklist_conformidade) ? out.checklist_conformidade : [],
          bases_juridicas_citadas: Array.isArray(out.bases_juridicas_citadas) ? out.bases_juridicas_citadas : [],
        };
        setIaMeta(iaMetaNext);

        if (editalId && manifestacaoId) {
          const snapshotAnalise = {
            v: 1,
            tipo: 'impugnacao',
            salvoEm: new Date().toISOString(),
            origem: 'ia',
            sugestaoImpugnacao: sugestaoImpugnacao.trim() || null,
            pontosSelecionados: [...ids],
            irregularidades: list,
            iaMeta: iaMetaNext,
            resumoTexto: `Impugnação — ${list.length} ponto(s) selecionado(s). ${iaMetaNext.resumo_executivo || ''}`.trim(),
          };
          try {
            await entities.Manifesto.patchManifestacao(editalId, manifestacaoId, {
              observacoes: JSON.stringify(snapshotAnalise),
            });
          } catch (snapErr) {
            console.warn('Análise IA: observações não gravadas no servidor:', snapErr);
            toast.warning(
              'Análise concluída, mas o resumo não foi sincronizado. Use «Salvar» para gravar tudo no servidor.'
            );
          }
        }

        if (textoMinutaCompleto.trim() && editalId && manifestacaoId) {
          try {
            await entities.Manifesto.postPecaVersao(editalId, manifestacaoId, {
              conteudo: textoMinutaCompleto.trim(),
              origem: 'ia',
            });
          } catch (persistErr) {
            console.warn('Minuta IA não persistida como versão:', persistErr);
            toast.warning('Minuta gerada; não foi possível gravar versão automaticamente (salve o rascunho se precisar).');
          }
        }

        queryClient.invalidateQueries({ queryKey: ['manifesto', 'detail', editalId, manifestacaoId] });
        queryClient.invalidateQueries({ queryKey: ['manifesto', 'list', editalId] });

        toast.success(
          list.length
            ? `Análise concluída: ${list.length} irregularidade(s) identificada(s).`
            : 'Análise concluída: nenhuma irregularidade clara; revise pedidos de esclarecimento na minuta.'
        );
        setStepIndex((prev) => (prev === 1 ? 2 : prev));
      } catch (e) {
        if (e?.name === 'AbortError') return;
        console.error('Impugnação IA:', e);
        const st = e?.status;
        const isIndisponivel = e?.isConnectionError || st === 0 || st === 502 || st === 503 || st === 504;
        toast.error(isIndisponivel ? ANALISE_IA_INDISPONIVEL_MSG : getDisplayMessage(e, 'Erro ao analisar os documentos.'));
      } finally {
        runInFlightRef.current = false;
        setIaLoading(false);
      }
    },
    [attachments, editalId, manifestacaoId, editalLabel, sugestaoImpugnacao, qualificacaoParagrafo, queryClient]
  );

  useEffect(() => {
    if (stepIndex < 2 || !qualificacaoParagrafo) return;
    setMinutaText((prev) => {
      if (prev.trim()) return prev;
      return `${qualificacaoParagrafo}\n\n`;
    });
  }, [stepIndex, qualificacaoParagrafo, manifestacaoId]);

  function limparEstadoFormulario() {
    setSugestaoImpugnacao('');
    setIssues([]);
    setSelected(new Set());
    setMinutaText('');
    setIaMeta(null);
    setStepIndex(0);
  }

  useEffect(() => {
    setAttachments([]);
    limparEstadoFormulario();
    ultimoSnapshotServidorRef.current = null;
  }, [manifestacaoId]);

  useEffect(() => {
    if (!manifestacaoId || !detalheServidor || String(detalheServidor.id) !== String(manifestacaoId)) {
      return;
    }
    const snap = buildManifestoDetailSnapshot(manifestacaoId, detalheServidor);
    if (snap == null) return;
    if (ultimoSnapshotServidorRef.current === snap) return;
    ultimoSnapshotServidorRef.current = snap;

    const obsPayload = parseObservacoesImpugnacaoSalvas(detalheServidor.observacoes);
    const pecasList = detalheServidor.pecas ?? [];
    const latest = pecasList[0];
    const conteudo = normalizarConteudoMinutaServidor(latest?.conteudo);

    if (obsPayload) {
      setSugestaoImpugnacao(typeof obsPayload.sugestaoImpugnacao === 'string' ? obsPayload.sugestaoImpugnacao : '');
      const irr = Array.isArray(obsPayload.irregularidades) ? obsPayload.irregularidades : [];
      const restored = restoreIssuesFromStorage(irr);
      setIssues(restored);
      const pts = Array.isArray(obsPayload.pontosSelecionados) ? obsPayload.pontosSelecionados : [];
      if (pts.length) setSelected(new Set(pts.map(String)));
      else if (restored.length) setSelected(new Set(restored.map((i) => i.id)));
      else setSelected(new Set());
      setIaMeta(obsPayload.iaMeta && typeof obsPayload.iaMeta === 'object' ? obsPayload.iaMeta : null);
    } else {
      setSugestaoImpugnacao('');
      setIssues([]);
      setSelected(new Set());
      setIaMeta(null);
    }

    setMinutaText(conteudo);

    const temAnaliseImpugnacao =
      (obsPayload?.irregularidades?.length ?? 0) > 0 ||
      (obsPayload?.iaMeta && String(obsPayload.iaMeta?.resumo_executivo || '').trim()) ||
      obsPayload?.origem === 'ia';
    const temPersistido = conteudo.length > 0 || temAnaliseImpugnacao;
    setStepIndex(temPersistido ? 2 : 0);
  }, [manifestacaoId, detalheServidor]);

  useEffect(() => {
    if (stepIndex !== 3) return;
    const t = window.setTimeout(() => {
      document.getElementById('manifesto-etapa-protocolo')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 150);
    return () => window.clearTimeout(t);
  }, [stepIndex]);

  function openFilePicker(slot) {
    pickSlotRef.current = slot;
    fileInputRef.current?.click();
  }

  async function handleFileSelected(ev) {
    const file = ev.target.files?.[0];
    ev.target.value = '';
    const slot = pickSlotRef.current;
    pickSlotRef.current = null;
    if (!file || !slot) return;

    const lower = file.name.toLowerCase();
    if (!lower.endsWith('.pdf')) {
      toast.error('Envie apenas arquivos PDF. A extração automática de texto usa o processador de PDF.');
      return;
    }

    const id = newId();
    const nome = file.name;

    if (slot === 'outros') {
      setAttachments((prev) => [...prev, { id, slot, nome, file_url: null, uploading: true }]);
    } else {
      setAttachments((prev) => [...prev.filter((a) => a.slot !== slot), { id, slot, nome, file_url: null, uploading: true }]);
    }

    try {
      const { file_url } = await uploadFile(file);
      if (!file_url) throw new Error('Upload sem URL');
      setAttachments((prev) => prev.map((a) => (a.id === id ? { ...a, file_url, uploading: false } : a)));
      toast.success(`${SLOT_META[slot]?.label || 'Documento'} enviado.`);
    } catch (err) {
      setAttachments((prev) => prev.filter((a) => a.id !== id));
      toast.error(getDisplayMessage(err, 'Falha no envio do arquivo.'));
    }
  }

  function removeAttachment(id) {
    setAttachments((prev) => prev.filter((a) => a.id !== id));
  }

  function handleAdvance() {
    if (stepIndex === 0) {
      if (!hasReadyPdfs || uploadingAny) {
        toast.error('Envie ao menos um PDF e aguarde o término dos envios antes de continuar.');
        return;
      }
    }
    if (stepIndex === 1) {
      const podeIrRevisao =
        !iaLoading &&
        (iaMeta != null || minutaText.trim().length > 0 || issues.length > 0);
      if (!podeIrRevisao) {
        toast.error('Execute «Realizar análise IA» antes de revisar a minuta.');
        return;
      }
    }
    if (stepIndex === 2 && !minutaText.trim()) {
      const base = issues
        .filter((i) => selected.has(i.id))
        .map((i, idx) => `${idx + 1}. ${i.titulo}${i.fundamento ? ` (${i.fundamento})` : ''}`)
        .join('\n');
      const refLinha = editalLabel?.trim() || '[MODALIDADE E NÚMERO DA LICITAÇÃO — conforme edital]';
      const cabFallback = `AO ILUSTRÍSSIMO(A) SENHOR(A) PREGOEIRO(A) / AGENTE DE CONTRATAÇÃO
[NOME E CARGO — conforme edital]

[NOME DO ÓRGÃO PÚBLICO — conforme edital]

REF.: ${refLinha}

PROCESSO ADMINISTRATIVO Nº [conforme edital — preencher após conferência]`;
      const fatosBloco =
        base ||
        'Sem pontos selecionados — descreva os fatos com âncora no edital ou refaça a análise IA para preencher automaticamente.';
      const corpo = `1. DA TEMPESTIVIDADE

A impugnação é tempestiva face ao prazo legal e ao cronograma do certame constante do instrumento convocatório, na forma da legislação aplicável (Lei nº 14.133/2021 ou Lei nº 8.666/1993, conforme o edital).

2. DOS FATOS

${fatosBloco}

3. DO MÉRITO / FUNDAMENTAÇÃO JURÍDICA

Com fundamento na Lei nº 14.133/2021 e/ou na Lei nº 8.666/1993, conforme aplicável ao certame, e na jurisprudência administrativa pertinente, requer-se a adequação do instrumento convocatório nos termos dos fatos narrados.

4. DOS PEDIDOS

1. Recebimento e conhecimento da impugnação.
2. Adequação do instrumento convocatório para preservação da isonomia e da competitividade.
3. Publicação de retificação ou esclarecimentos necessários, com efeitos úteis, na forma do direito.`;
      setMinutaText(montarMinutaImpugnacaoCompleta(cabFallback, qualificacaoParagrafo, corpo));
    }
    const next = Math.min(stepIndex + 1, FASES_IMPUGNACAO.length - 1);
    setStepIndex(next);
    if (typeof onAvancar === 'function') onAvancar(next);
  }

  function handleVoltarFase() {
    if (stepIndex <= 0) return;
    setStepIndex((s) => Math.max(0, s - 1));
  }

  const advanceLabel =
    stepIndex === 0
      ? 'Continuar para análise pela IA'
      : stepIndex === 1
        ? 'Ir para revisar a minuta'
        : stepIndex === 2
          ? 'Avançar para baixar e protocolar'
          : '';

  const singleSlots = ['edital', 'etp', 'termo_referencia'];
  const outrosList = attachments.filter((a) => a.slot === 'outros');
  const singleAttachments = (key) => attachments.find((a) => a.slot === key);

  return (
    <Card className="max-w-full overflow-x-hidden">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <ShieldAlert className="h-5 w-5 text-blue-600" />
          Impugnação ao Edital
        </CardTitle>
      </CardHeader>
      <CardContent className="min-w-0 max-w-full space-y-4 overflow-x-hidden">
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,application/pdf"
          className="hidden"
          onChange={handleFileSelected}
        />

        <StepperImpugnacao currentIndex={stepIndex} />

        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 space-y-3">
          <div>
            <p className="text-xs font-semibold text-blue-900">{etapaTitulo}</p>
            <p className="text-sm mt-1">{etapaDescricao}</p>
          </div>
        </div>

        {stepIndex === 0 ? (
        <div className="rounded-lg border border-slate-200 bg-white p-3 space-y-3">
            <p className="text-sm font-medium text-slate-900">Enviar documentos para análise</p>
            <p className="text-xs text-slate-600">
              Inclua o edital, ETP, Termo de Referência e outros PDFs relevantes. A fase seguinte usará somente o texto
              extraído destes arquivos.
            </p>
            <div className="grid gap-3 sm:grid-cols-1 md:grid-cols-2">
              {singleSlots.map((slot) => {
                const meta = SLOT_META[slot];
                const att = singleAttachments(slot);
                return (
                  <div key={slot} className="rounded-md border border-slate-200 p-3 space-y-2">
                    <Label className="text-xs font-semibold text-slate-700">{meta.label}</Label>
                    {att ? (
                      <div className="flex items-center justify-between gap-2 text-xs">
                        <span className="truncate text-slate-800" title={att.nome}>
                          {att.uploading ? (
                            <span className="flex items-center gap-1 text-slate-500">
                              <Loader2 className="h-3 w-3 animate-spin" />
                              Enviando…
                            </span>
                          ) : (
                            att.nome
                          )}
                        </span>
                        <Button type="button" variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => removeAttachment(att.id)} disabled={att.uploading}>
                          <Trash2 className="h-3.5 w-3.5 text-slate-500" />
                        </Button>
                      </div>
                    ) : null}
                    <Button type="button" variant="outline" size="sm" className="w-full" onClick={() => openFilePicker(slot)} disabled={iaLoading}>
                      <FileUp className="h-4 w-4 mr-2" />
                      {att ? 'Substituir PDF' : 'Enviar PDF'}
                    </Button>
                  </div>
                );
              })}
            </div>
            <div className="rounded-md border border-slate-200 p-3 space-y-2">
              <Label className="text-xs font-semibold text-slate-700">{SLOT_META.outros.label}</Label>
              {outrosList.length ? (
                <ul className="space-y-2">
                  {outrosList.map((att) => (
                    <li key={att.id} className="flex items-center justify-between gap-2 text-xs rounded bg-slate-50 px-2 py-1.5">
                      <span className="truncate" title={att.nome}>
                        {att.uploading ? (
                          <span className="flex items-center gap-1 text-slate-500">
                            <Loader2 className="h-3 w-3 animate-spin" />
                            Enviando…
                          </span>
                        ) : (
                          att.nome
                        )}
                      </span>
                      <Button type="button" variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => removeAttachment(att.id)} disabled={att.uploading}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-slate-500">Nenhum documento adicional. Opcional.</p>
              )}
              <Button type="button" variant="outline" size="sm" onClick={() => openFilePicker('outros')} disabled={iaLoading}>
                <FileUp className="h-4 w-4 mr-2" />
                Adicionar PDF
              </Button>
            </div>
        </div>
        ) : null}

        {stepIndex === 1 ? (
          <div className="rounded-lg border border-slate-200 bg-white p-3 space-y-4">
            <div>
              <p className="text-sm font-medium text-slate-900">Documentos para esta análise</p>
              <ul className="mt-2 space-y-1 text-xs text-slate-700">
                {sortAttachmentsForInvoke(attachments).length ? (
                  sortAttachmentsForInvoke(attachments).map((a) => (
                    <li key={a.id}>
                      <span className="font-medium text-slate-800">{SLOT_META[a.slot]?.label || a.slot}:</span>{' '}
                      {a.nome}
                      {a.uploading ? <span className="text-slate-500"> (enviando…)</span> : null}
                    </li>
                  ))
                ) : (
                  <li className="text-amber-800">Nenhum PDF pronto. Volte à fase anterior e envie os arquivos.</li>
                )}
              </ul>
            </div>
            <div className="space-y-2">
              <Label htmlFor="sugestao-impugnacao" className="text-xs font-semibold text-slate-700">
                Sugestão de Impugnação (opcional)
              </Label>
              <p className="text-xs text-slate-500">
                Orientação para a IA (ex.: exigências desproporcionais). A análise permanece ancorada nos PDFs enviados.
              </p>
              <Textarea
                id="sugestao-impugnacao"
                rows={4}
                className="text-sm"
                value={sugestaoImpugnacao}
                onChange={(e) => setSugestaoImpugnacao(e.target.value)}
                placeholder="Ex.: Enfatizar eventual exigência desproporcional de certificações em relação ao objeto."
                disabled={iaLoading}
              />
            </div>
            <div className="space-y-2">
              <Button
                type="button"
                size="sm"
                className="bg-blue-700 hover:bg-blue-800 text-white data-[loading]:ring-2 data-[loading]:ring-blue-300 data-[loading]:ring-offset-2 data-[loading]:ring-offset-background"
                disabled={!canRunIa}
                loading={iaLoading}
                onClick={() => void runImpugnacaoIa()}
              >
                {iaLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Processando…
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Realizar análise IA
                  </>
                )}
              </Button>
              {!hasReadyPdfs ? (
                <p className="text-xs text-amber-800">É necessário ao menos um PDF enviado na fase anterior.</p>
              ) : uploadingAny ? (
                <p className="text-xs text-slate-600">Aguarde o término dos envios antes de analisar.</p>
              ) : null}
              {iaLoading ? (
                <div className="rounded-md border border-blue-100 bg-white p-3 space-y-2">
                  <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-slate-700">
                    <span className="font-medium">Status do processamento</span>
                    <span className="font-mono tabular-nums text-slate-600">Tempo: {formatElapsed(elapsedSeconds)}</span>
                  </div>
                  <p className="text-xs text-slate-600">{processStatusLabel}</p>
                  <Progress value={Math.min(95, 8 + elapsedSeconds * 1.4)} className="h-2" />
                </div>
              ) : null}
            </div>
          </div>
        ) : null}

        {stepIndex >= 2 ? (
          <div className="rounded-lg border bg-muted/20 p-3">
            <p className="text-sm font-medium">Minuta de impugnação (prévia)</p>
            <p className="text-xs text-slate-600 mt-1">
              O preâmbulo (qualificação do impugnante) usa <strong>Empresas</strong> → <strong>Dados da Empresa</strong>:
              razão social, CNPJ, endereço, cargo e nome do representante (sócio); RG e CPF vêm dos campos de documento do
              responsável no cadastro da empresa, quando existirem. O restante é o corpo da peça (PDFs / IA). Ajuste antes do
              protocolo.
            </p>
            <Textarea
              rows={12}
              className="mt-3 min-w-0 max-w-full text-sm font-mono break-words"
              value={minutaText}
              onChange={(e) => setMinutaText(e.target.value)}
              placeholder="A minuta será preenchida após «Realizar análise IA» ou ao avançar com pontos selecionados."
            />
          </div>
        ) : null}

        {stepIndex >= 2 ? (
        <div className="grid min-w-0 gap-4 lg:grid-cols-[2fr_1fr]">
          <div className="grid gap-3">
            {iaLoading && !issues.length ? (
              <div className="rounded-lg border border-dashed p-8 text-center text-sm text-slate-600">
                <Loader2 className="h-8 w-8 mx-auto mb-2 animate-spin text-blue-600" />
                Processando documentação enviada…
              </div>
            ) : null}
            {!iaLoading && !issues.length ? (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                Nenhuma irregularidade listada ainda. Envie os PDFs, use «Realizar análise IA» e revise os achados aqui.
              </div>
            ) : null}
            {issues.map((item) => {
              const checked = selected.has(item.id);
              return (
                <label key={item.id} className="rounded-lg border p-3 block cursor-pointer">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-start gap-2">
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(v) => {
                          const next = new Set(selected);
                          if (v) next.add(item.id);
                          else next.delete(item.id);
                          setSelected(next);
                        }}
                      />
                      <div>
                        <p className="font-medium text-sm">{item.titulo}</p>
                        {item.fundamento ? <p className="text-xs text-slate-600 mt-1">{item.fundamento}</p> : null}
                        {item.detalhe ? (
                          <p className="text-xs text-slate-700 mt-2 whitespace-pre-wrap">{item.detalhe}</p>
                        ) : null}
                        {item.pedido ? <p className="text-xs text-slate-600 mt-2 italic">Pedido sugerido: {item.pedido}</p> : null}
                      </div>
                    </div>
                    <Badge variant={item.severidade === 'Alta' ? 'destructive' : 'secondary'}>{item.severidade}</Badge>
                  </div>
                </label>
              );
            })}
          </div>

          <div className="space-y-3">
            <div className="rounded-lg border bg-slate-50 p-3">
              <p className="text-xs text-slate-500">Resumo da análise IA</p>
              {iaMeta?.resumo_executivo ? (
                <p className="text-sm mt-2 min-w-0 text-slate-800 whitespace-pre-wrap break-words [overflow-wrap:anywhere]">
                  {iaMeta.resumo_executivo}
                </p>
              ) : (
                <p className="text-sm mt-2 text-slate-500">Realize a análise após o upload para ver o resumo fundamentado.</p>
              )}
              <div className="mt-3 flex flex-wrap gap-2 text-xs text-slate-600">
                {iaMeta?.confianca_0_100 != null ? (
                  <Badge variant="outline">Confiança ~{Math.round(iaMeta.confianca_0_100)}%</Badge>
                ) : null}
                {iaMeta?.campos_edital_utilizados != null ? (
                  <Badge variant="outline">{iaMeta.campos_edital_utilizados} trechos nos PDFs</Badge>
                ) : null}
                {issues.length ? <Badge variant="outline">{issues.length} achado(s)</Badge> : null}
              </div>
            </div>
            <div className="rounded-lg border bg-slate-50 p-3">
              <p className="text-sm font-medium">Checklist de conformidade</p>
              {iaMeta?.checklist_conformidade?.length ? (
                <ul className="text-xs text-slate-600 mt-2 space-y-2">
                  {iaMeta.checklist_conformidade.map((c, i) => (
                    <li key={i} className="flex gap-2">
                      <span>{c.atendido ? '✓' : '○'}</span>
                      <span>
                        {c.item}
                        {c.observacao ? <span className="block text-slate-500 mt-0.5">{c.observacao}</span> : null}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-slate-500 mt-2">Preenchido após a análise IA.</p>
              )}
            </div>
            <div className="rounded-lg border bg-slate-50 p-3">
              <p className="text-sm font-medium">Fundamentação legal completa</p>
              {iaMeta?.bases_juridicas_citadas?.length ? (
                <ul className="text-xs text-slate-600 mt-2 space-y-1 list-disc pl-4">
                  {iaMeta.bases_juridicas_citadas.map((b, i) => (
                    <li key={i}>{b}</li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-slate-600 mt-2">
                  Lei nº 14.133/2021, jurisprudência do TCU e doutrina especializada — referências específicas aparecem após a
                  análise e na minuta.
                </p>
              )}
            </div>
          </div>
        </div>
        ) : null}

        <div className="flex flex-col gap-3 border-t pt-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
          <p className="min-w-0 w-full flex-1 text-xs text-slate-600 break-words [overflow-wrap:anywhere] leading-relaxed sm:pr-2">
            {resumo}
          </p>
          <div className="flex w-full shrink-0 flex-wrap gap-2 sm:w-auto sm:justify-end">
            {stepIndex > 0 ? (
              <Button type="button" variant="outline" size="sm" onClick={handleVoltarFase} disabled={iaLoading}>
                Voltar fase
              </Button>
            ) : null}
            <Button type="button" variant="secondary" size="sm" onClick={onSalvarRascunho}>
              Salvar rascunho
            </Button>
            <Button
              type="button"
              size="sm"
              className="gap-2"
              disabled={salvandoFinal || iaLoading || typeof onSalvarFinal !== 'function'}
              onClick={async () => {
                if (typeof onSalvarFinal !== 'function') return;
                const temAnaliseOuMinuta =
                  !!minutaText.trim() || issues.length > 0 || (iaMeta && Object.keys(iaMeta).length > 0);
                if (!temAnaliseOuMinuta) {
                  toast.error('Realize a análise IA ou preencha a minuta antes de salvar.');
                  return;
                }
                try {
                  await onSalvarFinal({
                    minutaConteudo: minutaText,
                    observacoesPayload: {
                      v: 1,
                      tipo: 'impugnacao',
                      salvoEm: new Date().toISOString(),
                      sugestaoImpugnacao: sugestaoImpugnacao.trim() || null,
                      pontosSelecionados: [...selected],
                      iaMeta: iaMeta || null,
                      irregularidades: issues,
                      resumoTexto: resumo,
                    },
                  });
                } catch {
                  /* toast no pai */
                }
              }}
            >
              {salvandoFinal ? <Loader2 className="h-4 w-4 animate-spin shrink-0" /> : null}
              Salvar
            </Button>
            {stepIndex < 3 && advanceLabel ? (
              <Button
                type="button"
                size="sm"
                onClick={handleAdvance}
                disabled={
                  iaLoading ||
                  (stepIndex === 0 && (!hasReadyPdfs || uploadingAny)) ||
                  (stepIndex === 1 && !iaMeta && !minutaText.trim() && issues.length === 0)
                }
              >
                {advanceLabel}
              </Button>
            ) : null}
          </div>
        </div>

        {stepIndex === 3 ? (
          <div id="manifesto-etapa-protocolo" className="scroll-mt-6 rounded-lg border border-blue-200 bg-blue-50 p-3">
            <p className="text-sm font-medium">Baixe e protocole</p>
            <p className="text-xs text-slate-700 mt-1 min-w-0 break-words [overflow-wrap:anywhere]">
              Revise a minuta final. Imprima no mesmo padrão das propostas ou baixe o texto em .DOCX para editar no Word.
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={async () => {
                  try {
                    await printManifestoMinutaPdf({
                      tipoLabel: 'Impugnação',
                      etapaLabel: 'Protocolo',
                      editalLabel,
                      minutaText,
                    });
                  } catch (e) {
                    toast.error(e?.message || 'Erro ao imprimir minuta.');
                  }
                }}
              >
                Imprimir minuta
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="gap-2"
                disabled={gerandoDocx}
                onClick={async () => {
                  if (!minutaText.trim()) {
                    toast.error('Preencha ou gere a minuta antes de exportar.');
                    return;
                  }
                  setGerandoDocx(true);
                  try {
                    await downloadManifestoMinutaDocx(minutaText, {
                      editalLabel,
                      tipoSuffix: 'impugnacao',
                    });
                    toast.success('Arquivo .DOCX baixado.');
                  } catch (e) {
                    toast.error(e?.message || 'Erro ao gerar .DOCX.');
                  } finally {
                    setGerandoDocx(false);
                  }
                }}
              >
                {gerandoDocx ? <Loader2 className="h-4 w-4 animate-spin shrink-0" /> : null}
                Gerar .DOCX
              </Button>
            </div>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
